import { ReactNode, useState } from "react";
import { cn } from "../../../utils/class-names";
import { ChevronDownIcon, MenuIcon } from "../../../icons";
import { ProtocolKey } from "@silo/types";
import { Button } from "../../button";
import { ProtocolDisplay } from "../../protocol-display";
import { MobileNavContext } from "./mobile-nav-context";
import { BackButton, Chevron, MenuContainer, MobileMenu } from "./mobile-menu";
import { NavBar } from "./components/nav-bar";
import { tv } from "tailwind-variants";
import { navLink, NavLinkContainer } from "./nav-link";
import { MobileMenuState } from "./types";
import { MainLogo } from "../main-logo";
import * as dialog from "@zag-js/dialog";
import { Portal } from "@reach/portal";
import { normalizeProps, useMachine } from "@zag-js/react";

const disconnectedTrigger = tv({
  base: "px-2.5 justify-center",
});

interface MobileNavProps {
  className?: string;
  logoLink: ReactNode;
  protocolKey: ProtocolKey;
  setProtocolKey: (value: ProtocolKey) => void;
  runtimeProtocolKeys: ProtocolKey[];
  primaryLinks: ReactNode;
  secondaryLinks: ReactNode;
  walletTrigger: ReactNode;
  walletFlow: ReactNode;
  rewardsTrigger: ReactNode;
  contextSlot?: ReactNode;
}

export function MobileNav({
  className,
  logoLink,
  protocolKey,
  setProtocolKey,
  runtimeProtocolKeys,
  primaryLinks,
  secondaryLinks,
  walletTrigger,
  walletFlow,
  rewardsTrigger,
  contextSlot,
}: MobileNavProps): JSX.Element {
  const [state, send] = useMachine(
    dialog.machine({ id: "mobile-menu", closeOnOutsideClick: false })
  );
  const api = dialog.connect(state, send, normalizeProps);

  const [initialState, setInitialState] = useState<MobileMenuState>("home");

  function openDialog(_initialState: MobileMenuState = "home"): void {
    setInitialState(_initialState);
    api.open();
  }

  function closeDialog(): void {
    api.close();
  }

  function openWalletFlow(): void {
    openDialog("walletFlow");
  }

  function openVersionFlow(): void {
    openDialog("versionFlow");
  }

  function openProtocolFlow(): void {
    openDialog("protocolFlow");
  }

  const mobileNavContext: MobileNavContext = {
    closeDialog,
    openWalletFlow,
    openHome: openDialog,
  };

  return (
    <MobileNavContext.Provider value={mobileNavContext}>
      {contextSlot}

      <header>
        <NavBar>
          {logoLink}

          <div className="flex items-stretch gap-5">
            <Button
              className={cn("py-2.5", className)}
              variant="tertiary"
              onClick={openVersionFlow}
            >
              v1
            </Button>

            <Button
              className={cn("p-2.5 flex items-center gap-2.5", className)}
              variant="tertiary"
              onClick={openProtocolFlow}
            >
              <ProtocolDisplay.Icon protocolKey={protocolKey} />

              <ChevronDownIcon className="w-7 h-7" />
            </Button>

            <button className="text-white" onClick={() => openDialog()}>
              <MenuIcon className="w-7 h-7" />
            </button>
          </div>
        </NavBar>
      </header>

      <Portal>
        <div
          className="fixed top-0 left-0 z-40 w-screen h-screen"
          {...api.backdropProps}
        >
          <div
            className="fixed top-0 left-0 z-50 w-screen h-screen bg-shale-900"
            {...api.containerProps}
          >
            <div className="h-full" {...api.contentProps}>
              <MobileMenu
                key={initialState}
                primaryLinks={primaryLinks}
                secondaryLinks={secondaryLinks}
                walletTrigger={walletTrigger}
                walletFlow={walletFlow}
                initialState={initialState}
                protocolKey={protocolKey}
                setProtocolKey={setProtocolKey}
                runtimeProtocolKeys={runtimeProtocolKeys}
                rewardsTrigger={rewardsTrigger}
              />
            </div>
          </div>
        </div>
      </Portal>
    </MobileNavContext.Provider>
  );
}

MobileNav.LogoLinkContent = MainLogo;
MobileNav.NavLinkContainer = NavLinkContainer;
MobileNav.navLink = navLink;
MobileNav.disconnectedTrigger = disconnectedTrigger;
MobileNav.MenuContainer = MenuContainer;
MobileNav.BackButton = BackButton;
MobileNav.Chevron = Chevron;
